import _memoize from 'lodash/memoize';
import { Lightning } from '@lightningjs/sdk';
import { HD, FULL_HD } from '@bedrock-rdk/constants';
import {
  makeThemeColor,
  getDarkColor,
  getLightColor,
  DARK_MODE,
  LIGHT_MODE,
  PRIMARY,
  SECONDARY,
  TERTIARY,
} from '@tornado-rdk/theme';
import { getFont, FONT } from '@tornado-rdk/typography';
import { WHITE, TRANSPARENT } from '../utils/colors';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';

export const defaultContext = makeDefaultStylingContext();

export const BORDER_DIAMETER = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const DOT_DIAMETER = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const VERTICAL_PADDING = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const BORDER_RIGHT_MARGIN = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const TEXT_LABEL_WRAP_WIDTH = {
  [HD]: 500,
  [FULL_HD]: 770,
};

export const colors = {
  base: {
    [LIGHT_MODE]: getLightColor(TERTIARY),
    [DARK_MODE]: getDarkColor(PRIMARY),
  },
  selected: makeThemeColor(SECONDARY),
};

export const getBaseFont = _memoize(screenSize => getFont(FONT.KINSHASA_1, screenSize));

export const getInitialHeight = _memoize(
  screenSize => getBaseFont(screenSize).lineHeight + VERTICAL_PADDING[screenSize] * 2,
);

export const getFinalHeight = _memoize(finalH => finalH + VERTICAL_PADDING[defaultContext.screenSize] * 2);

export const getRoundedTexture = (diameter, strokeColor, fillColor) =>
  Lightning.Tools.getRoundRect(
    diameter,
    diameter,
    Math.round(diameter / 2),
    strokeColor ? 1 : 0,
    strokeColor,
    true,
    fillColor,
  );

export const getBorderTexture = diameter => getRoundedTexture(diameter, WHITE, TRANSPARENT);

export const getDotTexture = diameter => getRoundedTexture(diameter, TRANSPARENT, WHITE);

export const getInitialStyle = (context = defaultContext) => {
  const { screenSize, colorMode } = context;

  const borderDiameter = BORDER_DIAMETER[screenSize];
  const dotDiameter = DOT_DIAMETER[screenSize];
  const center = Math.round((borderDiameter - dotDiameter) / 2);
  const font = getBaseFont(screenSize);
  const h = getInitialHeight(screenSize);

  return {
    h,
    Border: {
      w: borderDiameter,
      h: borderDiameter,
      y: Math.round((h - borderDiameter) / 2),
      texture: getBorderTexture(borderDiameter),
      color: colors.base[colorMode],
    },
    Dot: {
      w: dotDiameter,
      h: dotDiameter,
      x: center,
      y: Math.round((h - dotDiameter) / 2),
      texture: getDotTexture(dotDiameter),
      color: colors.base[colorMode],
      scale: 0,
    },
    Label: {
      x: borderDiameter + BORDER_RIGHT_MARGIN[screenSize],
      y: VERTICAL_PADDING[screenSize],
      text: {
        ...font,
        maxLines: 3,
        wordWrapWidth: TEXT_LABEL_WRAP_WIDTH[screenSize],
        verticalAlign: 'middle',
      },
    },
  };
};

export const getBaseStyle = _memoize(
  ({ screenSize, colorMode }) => ({
    Border: {
      smooth: {
        color: colors.base[colorMode],
      },
    },
    Dot: {
      smooth: {
        scale: 0,
        color: colors.base[colorMode],
      },
    },
    Label: {
      text: {
        ...getBaseFont(screenSize),
      },
    },
  }),
  cacheResolver,
);

export const getSelectedStyle = _memoize(
  ({ screenSize, colorMode }) => ({
    Border: {
      smooth: {
        color: colors.selected[colorMode],
      },
    },
    Dot: {
      smooth: {
        scale: 1,
        color: colors.selected[colorMode],
      },
    },
    Label: {
      text: {
        ...getFont(FONT.KINSHASA_2, screenSize),
      },
    },
  }),
  cacheResolver,
);
